<template>
  <div class="landing-page">
    <nav ref="navbar" class="landing-nav">
      <div class="landing-container landing-nav__inner">
        <router-link to="/" class="landing-nav__logo">
          <img src="@/assets/logo.svg" alt="MentorMe" class="landing-nav__logo-img" />
        </router-link>

        <div class="landing-nav__links">
          <a v-for="link in navLinks" :key="link.href" :href="link.href" class="landing-nav__link">{{ link.label }}</a>
        </div>

        <div class="landing-nav__actions">
          <router-link v-if="isAuthenticated" :to="platformRoute" class="landing-nav__user">
            {{ displayName }}
          </router-link>
          <template v-else>
            <router-link to="/login" class="landing-nav__login" style="padding: 9px 20px; font-size: 14px">Войти</router-link>
            <router-link to="/registration-guest" class="landing-btn-red" style="padding: 10px 22px; font-size: 14px">Регистрация</router-link>
          </template>
        </div>
      </div>
    </nav>

    <!-- Hero -->
    <section id="hero" ref="hero" class="landing-hero">
      <div class="landing-hero__grid" aria-hidden="true" />
      <div class="landing-hero__glow-top" aria-hidden="true" />
      <div class="landing-hero__glow-teal" aria-hidden="true" />
      <canvas ref="sparkles" class="landing-hero__canvas" aria-hidden="true" />

      <div class="landing-container landing-hero__content">
        <div class="landing-hero__headline">
          <div class="landing-chip" style="background: rgba(232, 25, 44, 0.22); color: #ff7a87; margin-bottom: 24px">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
            EdTech-платформа №1 в Казахстане
          </div>

          <h1 class="landing-hero__title">
            Твой проводник к поступлению в <span style="color: #e8192c">ведущие зарубежные вузы</span>
          </h1>

          <p class="landing-hero__subtitle">
            EdTech-платформа, где собраны все материалы для подготовки и поступления в лучшие университеты мира — со стипендией
            <strong style="color: #fff">Болашак</strong> и международными грантами.
          </p>

          <div class="landing-hero__cta">
            <router-link to="/registration-guest" class="landing-btn-red" style="padding: 17px 36px; font-size: 16px">Перейти в платформу →</router-link>
            <a href="#account" class="landing-btn-ghost-white" style="padding: 16px 30px; font-size: 16px">Возможности платформы</a>
          </div>

          <div class="landing-hero__social-proof">
            <div class="landing-hero__avatars">
              <div
                v-for="(avatar, i) in heroAvatars"
                :key="avatar"
                class="landing-hero__avatar"
                :style="{ background: avatar.bg, zIndex: 5 - i }"
              >
                {{ avatar.letter }}
              </div>
            </div>
            <span style="font-size: 13.5px; color: rgba(255, 255, 255, 0.6); font-weight: 600">1000+ студентов поступили · ⭐ 4.9 / 5</span>
          </div>
        </div>

        <div ref="tiltWrap" class="landing-tilt-wrap">
          <div ref="tiltCard" class="landing-tilt-card">
            <div class="landing-tilt-frame">
              <div class="landing-mockbar landing-tilt-bar">
                <div class="landing-mockdot" style="background: #ff5f57" />
                <div class="landing-mockdot" style="background: #febc2e" />
                <div class="landing-mockdot" style="background: #28c840" />
                <div class="landing-tilt-url">🔒 platform.mentorme.kz</div>
              </div>
              <ImageSlot
                :src="screenshots.hero"
                placeholder="Скриншот платформы (Личный кабинет / Поиск программ)"
                fit="contain"
                height="520px"
              />
            </div>

            <div class="landing-sticker landing-sticker--top">
              <span style="font-size: 24px">🎓</span>
              <div>
                <div style="font-size: 13px; font-weight: 900; color: #111827">37 стипендий</div>
                <div style="font-size: 11px; color: #9ca3af">за 1 поток · 3 в год</div>
              </div>
            </div>
            <div class="landing-sticker landing-sticker--bottom">
              <span style="font-size: 24px">🌍</span>
              <div>
                <div style="font-size: 13px; font-weight: 900; color: #111827">1000+ программ</div>
                <div style="font-size: 11px; color: #9ca3af">в 30+ странах</div>
              </div>
            </div>
          </div>
        </div>

        <p style="font-size: 11.5px; color: rgba(255, 255, 255, 0.3); text-align: center; padding-bottom: 36px; max-width: 680px; margin: 0 auto; line-height: 1.5">
          *Платформа Mentor Me AI Tech зарегистрирована в реестре прав на объекты, охраняемые авторским правом Республики Казахстан №16003 от 15 февраля 2025 года.
        </p>
      </div>
    </section>

    <!-- Stats -->
    <section style="background: #fff; border-bottom: 1px solid var(--border)">
      <div class="landing-container landing-stats">
        <div v-for="stat in stats" :key="stat.label" class="landing-stat">
          <div class="landing-stat__value">{{ stat.value }}</div>
          <div class="landing-stat__label">{{ stat.label }}</div>
        </div>
      </div>
    </section>

    <!-- University marquee -->
    <section style="background: var(--bg); padding: 30px 0; border-bottom: 1px solid var(--border)">
      <div style="text-align: center; font-size: 11px; font-weight: 700; color: #9ca3af; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 18px">
        ВУЗы, куда поступили наши студенты
      </div>
      <div ref="marquee" class="landing-marquee-wrap" />
    </section>

    <!-- Personal account -->
    <section id="account" class="landing-section" style="background: #fff">
      <div class="landing-container">
        <FeatureSection
          eyebrow="Личный кабинет"
          title="Управляй подготовкой<br>и поступай в топовые вузы"
          lead="Используй все возможности платформы, чтобы эффективно подготовиться и поступить в ведущие зарубежные университеты."
          :bullets="accountBullets"
          cta-text="Открыть личный кабинет →"
          cta-to="/registration-guest"
          :screenshot="screenshots.account"
          screenshot-placeholder="Скриншот: Личный кабинет"
        />
      </div>
    </section>

    <!-- Program search -->
    <section id="search" class="landing-section" style="background: var(--bg)">
      <div class="landing-container">
        <FeatureSection
          reverse
          eyebrow="Поиск программ"
          title="Найди идеальный<br>университет и грант"
          lead="Удобный поиск на платформе Mentor Me поможет подобрать подходящую образовательную программу и поступить в ведущие зарубежные вузы."
          :bullets="searchBullets"
          cta-text="Начать поиск →"
          cta-to="/registration-guest"
          :screenshot="screenshots.search"
          screenshot-placeholder="Скриншот: Поиск программ"
        />
      </div>
    </section>

    <!-- Program details -->
    <section class="landing-section" style="background: #fff">
      <div class="landing-container">
        <FeatureSection
          eyebrow="Детальная информация"
          title="Знай всё о программе<br>перед подачей"
          lead="На платформе Mentor Me можно получить полную информацию о выбранной образовательной программе."
          :bullets="detailsBullets"
          cta-text="Посмотреть пример →"
          cta-to="/login"
          cta-ghost
          :screenshot="screenshots.details"
          screenshot-placeholder="Скриншот: Страница программы"
        />
      </div>
    </section>

    <!-- Bolashak -->
    <section id="bolashak" class="landing-bolashak">
      <div class="landing-container">
        <div class="landing-bolashak__grid">
          <div class="landing-fade-up">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px">
              <span style="font-size: 44px">🇰🇿</span>
              <div class="landing-chip" style="background: rgba(0, 174, 239, 0.2); color: #40c8f4">Государственная стипендия</div>
            </div>
            <h2 style="font-size: 44px; font-weight: 900; color: #fff; line-height: 1.12; letter-spacing: -0.8px; margin-bottom: 18px">
              Получи стипендию<br><span style="color: #00aeef">Болашак</span>
            </h2>
            <p style="font-size: 17px; color: rgba(255, 255, 255, 0.7); line-height: 1.7; margin-bottom: 32px">
              37 наших студентов получили Болашак за один поток. В году проходит 3 потока — мы знаем каждый шаг процесса и ведём вас от анкеты до оффера.
            </p>
            <div class="landing-bolashak__stats">
              <div v-for="item in bolashakStats" :key="item.label" class="landing-bolashak__stat-card">
                <div style="font-size: 28px; font-weight: 900; color: #00aeef; letter-spacing: -0.5px; margin-bottom: 4px">{{ item.value }}</div>
                <div style="font-size: 13px; color: rgba(255, 255, 255, 0.5)">{{ item.label }}</div>
              </div>
            </div>
            <router-link to="/registration-guest" class="landing-btn-red" style="padding: 16px 36px; font-size: 16px">Узнать подробнее →</router-link>
          </div>

          <div class="landing-fade-up landing-bolashak__steps">
            <div v-for="(step, i) in bolashakSteps" :key="step" class="landing-bolashak__step" :class="{ 'landing-bolashak__step--final': i === bolashakSteps.length - 1 }">
              <div
                :style="{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: i === bolashakSteps.length - 1 ? 'var(--red)' : 'rgba(0,174,239,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: i === bolashakSteps.length - 1 ? '18px' : '16px',
                  fontWeight: 900,
                  color: i === bolashakSteps.length - 1 ? '#fff' : '#00aeef',
                  flexShrink: 0,
                }"
              >
                {{ i === bolashakSteps.length - 1 ? '🎓' : i + 1 }}
              </div>
              <span :style="{ fontSize: '14.5px', fontWeight: i === bolashakSteps.length - 1 ? 700 : 600, color: '#fff' }">{{ step }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Language school -->
    <section id="school" class="landing-section" style="background: #fff">
      <div class="landing-container">
        <FeatureSection
          reverse
          eyebrow="Языковая школа"
          eyebrow-color="#d97706"
          check-color="#d97706"
          check-bg="rgba(245,158,11,0.12)"
          title="Подтяни английский<br>для поступления"
          lead="Освой английский с профессиональными преподавателями и подготовься к поступлению в топовые университеты мира."
          :bullets="schoolBullets"
          cta-text="Записаться на урок →"
          cta-to="/registration-guest"
          :screenshot="screenshots.school"
          screenshot-placeholder="Скриншот: Языковая школа"
        />
      </div>
    </section>

    <!-- Exams -->
    <section class="landing-section" style="background: var(--bg)">
      <div class="landing-container">
        <FeatureSection
          eyebrow="Международные экзамены"
          eyebrow-color="#7c3aed"
          check-color="#7c3aed"
          check-bg="rgba(124,58,237,0.1)"
          title="Подготовься к IELTS,<br>SAT, GRE и KazTest"
          lead="Хочешь высокий балл на IELTS, GRE, KazTest или других экзаменах? Начни подготовку уже сегодня!"
          :bullets="examBullets"
          cta-text="Пройти пробный тест →"
          cta-to="/registration-guest"
          :screenshot="screenshots.exam"
          screenshot-placeholder="Скриншот: Подготовка к экзаменам"
        />
      </div>
    </section>

    <!-- Grants -->
    <section class="landing-section" style="background: #fff">
      <div class="landing-container">
        <FeatureSection
          reverse
          eyebrow="Международные гранты"
          eyebrow-color="var(--teal)"
          check-color="#0bbfad"
          check-bg="rgba(11,191,173,0.1)"
          title="Получи грант<br>на учёбу за рубежом"
          lead="Хочешь учиться за границей бесплатно или с минимальными затратами? Находи гранты, стипендии и финансовую поддержку."
          :bullets="grantsBullets"
          cta-text="Смотреть гранты →"
          cta-to="/login"
          :screenshot="screenshots.grants"
          screenshot-placeholder="Скриншот: Международные гранты"
        >
          <template #extra>
            <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 24px">
              <span v-for="tag in grantTags" :key="tag" class="landing-chip" style="background: var(--bg); color: var(--gray); border: 1px solid var(--border)">{{ tag }}</span>
            </div>
          </template>
        </FeatureSection>
      </div>
    </section>

    <!-- Community -->
    <section class="landing-section" style="background: var(--bg)">
      <div class="landing-container">
        <div class="landing-fade-up" style="text-align: center; max-width: 560px; margin: 0 auto 56px">
          <div class="landing-eyebrow" style="color: var(--red); margin-bottom: 14px">Сообщество</div>
          <h2 class="landing-h2">Community по странам обучения</h2>
          <p class="landing-lead">Обменивайся опытом и получай поддержку от студентов и экспертов из разных регионов мира.</p>
        </div>
        <div class="landing-community-grid">
          <div v-for="item in community" :key="item.title" class="landing-card landing-lift landing-fade-up" style="padding: 26px 20px; text-align: center">
            <div style="font-size: 40px; margin-bottom: 12px">{{ item.flag }}</div>
            <h4 style="font-size: 15px; font-weight: 800; color: var(--dark); margin-bottom: 4px">{{ item.title }}</h4>
            <p style="font-size: 12.5px; color: var(--gray)">{{ item.subtitle }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Rankings -->
    <section id="rankings" class="landing-section" style="background: #fff">
      <div class="landing-container">
        <div class="landing-fade-up" style="text-align: center; max-width: 600px; margin: 0 auto 56px">
          <div class="landing-eyebrow" style="color: var(--red); margin-bottom: 14px">Рейтинги</div>
          <h2 class="landing-h2">Выбирай вуз по мировым рейтингам</h2>
          <p class="landing-lead">Ведущие мировые рейтинги университетов помогут выбрать вуз по академической репутации и другим критериям.</p>
        </div>
        <div class="landing-rankings-grid">
          <div v-for="item in rankings" :key="item.title" class="landing-card landing-lift landing-fade-up" style="padding: 28px 24px">
            <div :style="item.iconStyle">{{ item.icon }}</div>
            <h4 style="font-size: 15.5px; font-weight: 800; color: var(--dark); margin-bottom: 6px">{{ item.title }}</h4>
            <p style="font-size: 13px; color: var(--gray); line-height: 1.5">{{ item.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="landing-section" style="background: var(--bg)">
      <div class="landing-container">
        <div class="landing-fade-up" style="text-align: center; max-width: 500px; margin: 0 auto 56px">
          <div class="landing-eyebrow" style="color: var(--red); margin-bottom: 14px">Отзывы</div>
          <h2 class="landing-h2">Истории наших студентов</h2>
        </div>
        <div class="landing-testimonials-grid">
          <div
            v-for="item in testimonials"
            :key="item.name"
            class="landing-card landing-lift landing-fade-up"
            :style="{ padding: '28px', background: item.dark ? 'var(--navy)' : '#fff' }"
          >
            <div style="color: #f59e0b; font-size: 16px; margin-bottom: 14px">★★★★★</div>
            <p :style="{ fontSize: '14.5px', color: item.dark ? 'rgba(255,255,255,0.85)' : 'var(--dark)', lineHeight: 1.7, marginBottom: '20px', fontStyle: 'italic' }">
              "{{ item.quote }}"
            </p>
            <div style="display: flex; align-items: center; gap: 12px">
              <div :style="{ width: '42px', height: '42px', borderRadius: '50%', background: item.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: '#fff', flexShrink: 0 }">
                {{ item.initial }}
              </div>
              <div>
                <div :style="{ fontSize: '14px', fontWeight: 700, color: item.dark ? '#fff' : 'var(--dark)' }">{{ item.name }}</div>
                <div :style="{ fontSize: '12px', color: item.dark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }">{{ item.meta }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="landing-cta">
      <div class="landing-container" style="position: relative; z-index: 1">
        <div class="landing-fade-up">
          <p style="font-size: 12px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255, 255, 255, 0.6); margin-bottom: 16px">Начни прямо сейчас</p>
          <h2 class="landing-cta__title">Готов поступить в зарубежный вуз?</h2>
          <p style="font-size: 18px; color: rgba(255, 255, 255, 0.85); margin-bottom: 40px">Зарегистрируйся бесплатно и получи доступ к 1000+ программам уже сегодня.</p>
          <div style="display: flex; gap: 16px; justify-content: center; flex-wrap: wrap">
            <router-link to="/registration-guest" class="landing-cta__btn-white">Перейти в платформу →</router-link>
            <router-link to="/login" class="landing-btn-ghost-white" style="padding: 16px 32px; font-size: 16px">Открыть демо</router-link>
          </div>
        </div>
      </div>
    </section>

    <LandingFooter />
  </div>
</template>

<script>
import ImageSlot from '@/components/landing/ImageSlot.vue';
import FeatureSection from '@/components/landing/FeatureSection.vue';
import LandingFooter from '@/components/landing/LandingFooter.vue';
import { mapGetters } from 'vuex';
import {
  initFadeUp,
  initHeroTilt,
  initNavbarTheme,
  initSparkles,
  initUniversityMarquee,
} from './landingEffects';

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800;900&display=swap';

function landingShot(file) {
  return `/screenshotsForMainPage/${encodeURIComponent(file)}`;
}

const LANDING_SCREENSHOTS = {
  hero: landingShot('главная страница.png'),
  account: landingShot('главная страница.png'),
  search: landingShot('поиск программ.png'),
  details: landingShot('программа.png'),
  school: landingShot('языковая школа.png'),
  exam: landingShot('тестовая школа.png'),
  grants: landingShot('меж гранты.png'),
};

export default {
  name: 'LandingPage',
  components: { ImageSlot, FeatureSection, LandingFooter },
  data() {
    return {
      screenshots: { ...LANDING_SCREENSHOTS },
      navLinks: [
        { href: '#account', label: 'Платформа' },
        { href: '#search', label: 'Поиск программ' },
        { href: '#bolashak', label: 'Болашак' },
        { href: '#school', label: 'Языковая школа' },
        { href: '#rankings', label: 'Рейтинги' },
      ],
      heroAvatars: [
        { letter: 'А', bg: 'linear-gradient(135deg,#E8192C,#ff6b35)' },
        { letter: 'Д', bg: 'linear-gradient(135deg,#7C3AED,#4F46E5)' },
        { letter: 'М', bg: 'linear-gradient(135deg,#059669,#0BBFAD)' },
        { letter: 'Н', bg: 'linear-gradient(135deg,#D97706,#F59E0B)' },
      ],
      stats: [
        { value: '1000+', label: 'студентов поступили' },
        { value: '37', label: 'стипендий за поток Болашак' },
        { value: '1000+', label: 'программ в базе' },
        { value: '30+', label: 'стран мира' },
      ],
      accountBullets: [
        { strong: 'Следи за статусом договора', text: '— вовремя продлевай доступ к материалам.' },
        { strong: 'Контролируй дедлайны', text: '— управляй программами и датами подачи.' },
        { strong: 'Готовься к экзаменам', text: '— проходи курсы и сдавай IELTS, GRE и другие тесты.' },
        { strong: 'Минимизируй риски', text: '— исправляй ошибки и повышай шансы на поступление.' },
        { strong: 'Дорабатывай документы', text: '— загружай резюме, эссе и рекомендательные письма.' },
      ],
      searchBullets: [
        { strong: 'Выбери международную стипендию', text: '— найди гранты и финансирование.' },
        { strong: 'Определи степень', text: '— бакалавриат, магистратура или подготовительные программы.' },
        { strong: 'Выбери категорию', text: '— IT, инженерия, бизнес, медицина и другие направления.' },
        { strong: 'Фильтруй по стране и требованиям', text: '— GPA, IELTS, GRE/GMAT, стоимость, длительность.' },
        { strong: 'Сравнивай программы', text: '— изучай детали, добавляй в избранное и подавай заявки.' },
      ],
      detailsBullets: [
        { strong: 'Описание университета', text: '— история, направления и академические особенности.' },
        { strong: 'Ключевые параметры', text: '— длительность, стоимость, GPA и языковые требования.' },
        { strong: 'Локация и рейтинг', text: '— страна, город, место в мировых рейтингах.' },
        { strong: 'Формат обучения', text: '— академический процесс, курсы и возможности для студентов.' },
      ],
      schoolBullets: [
        { strong: 'Выбери формат', text: '— индивидуально, в группе или онлайн.' },
        { strong: 'Запишись на бесплатный пробный урок', text: '— начни без риска.' },
        { strong: 'Практикуй разговорный английский', text: '— в Online Speaking Club.' },
        { strong: 'Доступ к книгам и гайдам', text: '— материалы для самостоятельной работы.' },
        { strong: 'Учись у опытных преподавателей', text: '— профессионалы, готовые помочь.' },
      ],
      examBullets: [
        { strong: 'Пройди пробный тест', text: '— узнай свой уровень.' },
        { strong: 'Выбери онлайн-курсы', text: '— готовься в удобном формате.' },
        { strong: 'Доступ к материалам и гайдам', text: '— учись в своём темпе.' },
        { strong: 'Учись у сертифицированных преподавателей', text: '— поддержка и рекомендации.' },
      ],
      grantsBullets: [
        { strong: 'Подбери подходящие программы', text: '— бакалавриат, магистратура, PhD.' },
        { strong: 'Изучай гайды и брошюры', text: '— узнай все детали подачи.' },
        { strong: 'Проходи онлайн-курсы', text: '— готовься к успешному поступлению.' },
        { strong: 'Следи за обновлениями', text: '— новые гранты появляются регулярно.' },
      ],
      grantTags: ['🇰🇿 Болашак', '🇪🇺 Erasmus Mundus', '🇬🇧 Chevening', '🇺🇸 Fulbright', '🇭🇺 Hungaricum'],
      bolashakStats: [
        { value: '100–150K $', label: 'в год на обучение' },
        { value: '37', label: 'стипендиатов за 1 поток' },
        { value: '3', label: 'потока Болашак в год' },
        { value: '100%', label: 'покрытие расходов' },
      ],
      bolashakSteps: [
        'Регистрация и оценка профиля',
        'Подбор программ под Болашак',
        'Подготовка пакета документов',
        'Подача заявки и интервью',
        'Получение стипендии Болашак!',
      ],
      community: [
        { flag: '🇺🇸', title: 'США', subtitle: 'Ivy League, STEM, MBA' },
        { flag: '🇬🇧', title: 'Великобритания', subtitle: 'Russell Group, 1 год' },
        { flag: '🇪🇺', title: 'Европа', subtitle: 'Erasmus, бесплатно' },
        { flag: '🇰🇷', title: 'Корея', subtitle: 'KOICA, GKS' },
        { flag: '🇭🇰', title: 'Гонконг', subtitle: 'HKU, гранты' },
      ],
      rankings: [
        {
          icon: 'QS',
          iconStyle: 'width:48px;height:48px;border-radius:12px;background:var(--rl);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:900;color:var(--red);margin-bottom:16px',
          title: 'QS World University Rankings',
          text: 'Один из самых авторитетных глобальных рейтингов вузов.',
        },
        {
          icon: 'THE',
          iconStyle: 'width:48px;height:48px;border-radius:12px;background:rgba(11,191,173,0.1);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:900;color:var(--teal);margin-bottom:16px',
          title: 'Times Higher Education',
          text: 'Рейтинг по качеству преподавания и исследований.',
        },
        {
          icon: '🏛',
          iconStyle: 'width:48px;height:48px;border-radius:12px;background:rgba(245,158,11,0.1);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:16px',
          title: 'Shanghai Ranking',
          text: 'Академический рейтинг университетов мира (ARWU).',
        },
        {
          icon: '🌐',
          iconStyle: 'width:48px;height:48px;border-radius:12px;background:rgba(59,130,246,0.1);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:16px',
          title: 'US News Best Global',
          text: 'Рейтинг лучших глобальных университетов мира.',
        },
      ],
      testimonials: [
        {
          quote: 'Получила Болашак и поступила в University of Leeds. MentorMe помогли с каждым этапом — от выбора программы до интервью.',
          name: 'Динара Сейткали',
          meta: 'University of Leeds · Болашак 2024',
          initial: 'Д',
          avatarBg: 'linear-gradient(135deg,#7C3AED,#4F46E5)',
        },
        {
          quote: 'Платформа очень удобная — все документы, дедлайны и программы в одном месте. Поступил в Penn State с первой попытки.',
          name: 'Аслан Табылдиев',
          meta: 'Penn State University · 2024',
          initial: 'А',
          avatarBg: 'linear-gradient(135deg,var(--red),#ff6b35)',
          dark: true,
        },
        {
          quote: 'Ментор помог написать SOP, который меня выделил. Сейчас учусь по Erasmus в Нидерландах!',
          name: 'Мадина Нурланова',
          meta: 'Leiden University · Erasmus 2024',
          initial: 'М',
          avatarBg: 'linear-gradient(135deg,#059669,#0BBFAD)',
        },
      ],
      cleanups: [],
      fontLinkEl: null,
    };
  },
  computed: {
    ...mapGetters(['userInfo']),
    isAuthenticated() {
      return Boolean(localStorage.getItem('access_token') && this.$store.state.auth);
    },
    displayName() {
      const user = this.userInfo;
      if (!user) return this.$store.state.user_name || 'Профиль';
      const parts = [user.name, user.surname].filter(Boolean);
      return parts.join(' ') || this.$store.state.user_name || 'Профиль';
    },
    platformRoute() {
      const role = this.$store.state.role;
      if (role === 'user') {
        return this.$store.state.hasApprovedPrograms ? '/platform' : '/programs';
      }
      if (role === 'guest') return '/platform';
      if (role === 'curator' || role === 'expert') return '/curator';
      if (role === 'supervisor' || role === 'chief') return '/supervisor';
      return '/platform';
    },
  },
  mounted() {
    document.title = 'MentorMe — Поступление в зарубежные вузы, стипендия Болашак, IELTS | EdTech-платформа';

    this.fontLinkEl = document.createElement('link');
    this.fontLinkEl.rel = 'stylesheet';
    this.fontLinkEl.href = FONT_LINK;
    document.head.appendChild(this.fontLinkEl);

    initUniversityMarquee(this.$refs.marquee);
    this.cleanups.push(initFadeUp());
    this.cleanups.push(initNavbarTheme(this.$refs.navbar, this.$refs.hero));
    this.cleanups.push(initHeroTilt(this.$refs.tiltCard, this.$refs.tiltWrap));
    this.cleanups.push(initSparkles(this.$refs.sparkles, this.$refs.hero));
  },
  unmounted() {
    this.cleanups.forEach((fn) => fn && fn());
    if (this.fontLinkEl) {
      this.fontLinkEl.remove();
    }
  },
};
</script>

<style lang="scss" src="./landing.scss"></style>
